import {
  NAME_MAX,
  warsawToday,
  type CalorieProductDto,
  type productBody,
} from '@iac/contracts';
import { useId, useMemo, useState, type FormEvent } from 'react';
import type { z } from 'zod';
import { DeleteButton } from '../../components/DeleteButton';
import { Field } from '../../components/Field';
import {
  IconCalendarPlus,
  IconPencilSquare,
  IconPlus,
  IconTrash,
} from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { Tabs } from '../../components/Tabs';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { toast } from '../../lib/toast';
import {
  useCreateProduct,
  useDeleteProduct,
  useLogProduct,
  useProducts,
  useUpdateProduct,
} from './api';
import { caloriesDict } from './i18n';
import { SwipeRow } from './SwipeRow';

type ProductFormValues = z.input<typeof productBody>;

/** Products and dishes: one catalog page, a tab (and a URL) each. */
export function CatalogTabs() {
  const t = useDict(caloriesDict);
  return (
    <Tabs
      label={t.catalogTabs}
      items={[
        { key: 'products', href: '/calories/products', label: t.products },
        { key: 'dishes', href: '/calories/dishes', label: t.dishes },
      ]}
    />
  );
}

const blank: ProductFormValues = { name: '', kcalPer100g: '', grams: '' };

function ProductForm({
  product,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  product?: CalorieProductDto;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: ProductFormValues, reset: () => void) => void;
  onCancel: () => void;
}) {
  const formId = useId();
  const t = useDict(caloriesDict);
  const [values, setValues] = useState<ProductFormValues>(() =>
    product
      ? {
          name: product.name,
          kcalPer100g:
            product.kcalPer100g === null ? '' : String(product.kcalPer100g),
          grams: product.grams === null ? '' : String(product.grams),
        }
      : blank,
  );
  const patch = (next: Partial<ProductFormValues>) =>
    setValues((current) => ({ ...current, ...next }));

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit(values, () => setValues(blank));
  }

  return (
    <form onSubmit={submit} className="entry-form">
      <div className="product-form-fields">
        <Field
          id={`${formId}-name`}
          label={t.name}
          type="text"
          autoComplete="off"
          required
          maxLength={NAME_MAX}
          autoFocus
          value={values.name}
          onChange={(e) => patch({ name: e.target.value })}
        />
        <Field
          id={`${formId}-kcal`}
          label="kcal/100g"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={values.kcalPer100g}
          onChange={(e) => patch({ kcalPer100g: e.target.value })}
        />
        <Field
          id={`${formId}-grams`}
          label={t.weight}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={values.grams}
          onChange={(e) => patch({ grams: e.target.value })}
        />
      </div>
      <div className="form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          {t.cancel}
        </button>
        <button type="submit" className="btn-primary" disabled={pending}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function ProductRow({ product }: { product: CalorieProductDto }) {
  const t = useDict(caloriesDict);
  const [editing, setEditing] = useState(false);
  const update = useUpdateProduct();
  const remove = useDeleteProduct();
  const log = useLogProduct();

  const confirmDelete = t.confirmDeleteProduct(product.name);
  // Read at the tap, not at render, so a page left open past midnight logs
  // to the new day.
  const logToday = () =>
    log.mutate(
      { id: product.id, date: warsawToday() },
      {
        onSuccess: () =>
          toast.success(t.loggedToday(product.name), {
            action: { label: t.viewDay, to: '/calories' },
          }),
      },
    );

  if (editing) {
    return (
      <li className="product-row product-row-form">
        <ProductForm
          product={product}
          submitLabel={t.save}
          pending={update.isPending}
          onSubmit={(values) =>
            update.mutate(
              { id: product.id, ...values },
              { onSuccess: () => setEditing(false) },
            )
          }
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <SwipeRow
      className="product-row"
      bodyClassName="product-row-body"
      startLabel={
        <>
          <IconCalendarPlus /> {t.addToToday}
        </>
      }
      deleteLabel={
        <>
          {t.delete} <IconTrash />
        </>
      }
      onRight={() => {
        if (!log.isPending) logToday();
      }}
      onLeft={() => {
        if (window.confirm(confirmDelete)) remove.mutate(product.id);
      }}
      onClick={() => setEditing(true)}
    >
      <div className="entry-main">
        <p className="entry-name">{product.name}</p>
        {product.grams !== null && (
          <p className="entry-detail">{product.grams} g</p>
        )}
      </div>
      <p className="product-kcal">{product.kcalPer100g ?? '—'}</p>
      {/* Desktop only; a phone swipes or taps instead. Clicks here mustn't
            also open the editor. */}
      <div className="product-actions" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.addToTodayItem(product.name)}
          title={t.addToToday}
          disabled={log.isPending}
          onClick={logToday}
        >
          <IconCalendarPlus />
        </button>
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.editItem(product.name)}
          title={t.edit}
          onClick={() => setEditing(true)}
        >
          <IconPencilSquare />
        </button>
        <DeleteButton
          label={t.deleteItem(product.name)}
          confirm={confirmDelete}
          onConfirm={() => remove.mutate(product.id)}
        />
      </div>
    </SwipeRow>
  );
}

/**
 * The catalog, A–Z. "Add to today" logs one of the product, outside any
 * meal, on today.
 */
export function CaloriesProducts() {
  const t = useDict(caloriesDict);
  usePageTitle(`${t.products} — ${t.titleSuffix}`);
  const formId = useId();
  const { data: products } = useProducts();
  const create = useCreateProduct();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');

  const shown = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('pl');
    return q === ''
      ? products
      : products?.filter((p) => p.name.toLocaleLowerCase('pl').includes(q));
  }, [products, search]);

  return (
    <>
      <PageHeader
        title={t.products}
        actions={
          !adding && (
            <button
              type="button"
              className="btn-primary-icon"
              aria-label={t.newProduct}
              title={t.newProduct}
              onClick={() => setAdding(true)}
            >
              <IconPlus />
            </button>
          )
        }
      />
      <CatalogTabs />

      {adding && (
        <Panel title={t.newProduct} tone="highlight">
          <ProductForm
            submitLabel={t.add}
            pending={create.isPending}
            onSubmit={(values, reset) =>
              create.mutate(values, { onSuccess: reset })
            }
            onCancel={() => setAdding(false)}
          />
        </Panel>
      )}

      <Panel>
        <Field
          id={`${formId}-search`}
          label={t.search}
          type="search"
          autoComplete="off"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Panel>

      <Panel className="product-list-card">
        {shown && shown.length > 0 ? (
          <>
            <div className="product-list-header" aria-hidden="true">
              <span className="entry-main">{t.product}</span>
              <span className="product-list-header-kcal">kcal/100g</span>
              <span className="product-actions-spacer" />
            </div>
            <ul className="product-list">
              {shown.map((product) => (
                <ProductRow key={product.id} product={product} />
              ))}
            </ul>
          </>
        ) : (
          <p className="product-list-empty">
            {products && products.length > 0 ? t.noMatch : t.catalogEmpty}
          </p>
        )}
      </Panel>
    </>
  );
}
